/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import React, { useState } from "react";

import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IconButton,
  IColumn,
  IDetailsRowProps,
  Modal,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Text,
} from "@fluentui/react";

import * as EventActions from "Dataflows/Event/EventAction";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import {
  detailListStyles,
  iconButtonStyles,
  modalHeaderStyles,
  modalStyles,
  spinnerStyles,
  ModalContent,
  ModalHeader,
  StyledSpan,
} from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import {
  getCurrentPage,
  getLoadEventsError,
  getPageSize,
  getSearchText,
  isLoadingEvents,
} from "Dataflows/Event/EventSelector";
import { AppState } from "Dataflows/Reducers";
import { IEvent } from "Interfaces/Event/IEvent";
import { formatDate } from "Utility/FormatHelper";

import { PaginationRow } from "../Common/Pagination/Pagination.styles";
import "../Common/Pagination/Pagination.styles.css";
import EventDetails from "../EventDetails/EventDetails";
import { IEventsListProps } from "./IEventsListProps";

/**
 * List of student profiles
 * @Component
 * @returns {React.FunctionComponent<IEventsListProps>}
 */
const EventsList: React.FunctionComponent<IEventsListProps> = (props: IEventsListProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data, actions, loadEventsError, isLoadingEvents, filteredCount, currentPage, pageSize } =
    props;

  const eventsToRender = data.slice(0, pageSize);

  const formatEventDate = (incomingDate: Date): React.ReactElement => {
    return <span>{formatDate(incomingDate)}</span>;
  };

  const formatStatusColumn = (statusKey: string): React.ReactElement => {
    return <StatusElement statusValue={statusKey} />;
  };

  const columns: IColumn[] = [
    {
      key: "id",
      name: "ID",
      ariaLabel: "Event ID",
      fieldName: "id",
      minWidth: 40,
      maxWidth: 50,
      isResizable: true,
    },
    {
      key: "title",
      name: "Title",
      ariaLabel: "Title of event",
      fieldName: "title",
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      isSorted: false,
    },
    {
      key: "createdBy",
      name: "Created By",
      ariaLabel: "Who created of event",
      fieldName: "createdBy",
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "eventType",
      name: "Event Type",
      ariaLabel: "Type of event",
      fieldName: "eventType",
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: "dateStart",
      name: "Start Date",
      ariaLabel: "Start date of event",
      fieldName: "dateStart",
      minWidth: 100,
      maxWidth: 110,
      onRender: (item: IEvent): React.ReactElement => {
        return formatEventDate(item.dateStart);
      },
      isResizable: true,
    },
    {
      key: "dateEnd",
      name: "End Date",
      ariaLabel: "End date of event",
      fieldName: "dateEnd",
      minWidth: 100,
      maxWidth: 110,
      onRender: (item: IEvent): React.ReactElement => {
        return formatEventDate(item.dateEnd);
      },
      isResizable: true,
    },
    {
      key: "eventStatus",
      name: "Event Status",
      ariaLabel: "Status",
      fieldName: "eventStatusName",
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: IEvent): React.ReactElement => {
        return formatStatusColumn(item.eventStatusName);
      },
      isResizable: true,
    },
    {
      key: "lastReviewedBy",
      name: "Last Reviewed By",
      ariaLabel: "Who reviewed the event",
      fieldName: "lastReviewedBy",
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
  ];

  const onClickEvent = (eventId: string): void => {
    actions.updateSelectedEventId(eventId);
    setIsModalOpen(true);
  };

  const onModalClose = (): void => {
    setIsModalOpen(false);
  };

  const onRenderColumn = (item: IEvent, index?: number, column?: IColumn): React.ReactElement => {
    const fieldContent = item[column?.fieldName as keyof IEvent] as string;
    if (column?.key == "id") {
      return <StyledSpan>{fieldContent}</StyledSpan>;
    }
    return <span>{fieldContent}</span>;
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const selectedEvent = props!.item as IEvent;

    // Allows for calculated values to be included in the item data rendered by list
    const calculatedItemProps = {
      ...selectedEvent,
      ...{ createdBy: selectedEvent.createdBy },
    };

    return defaultRender!({
      ...props,
      item: { ...calculatedItemProps },
      onClick: () => onClickEvent(selectedEvent.eventIdentifier),
    });
  };

  if (isLoadingEvents) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (loadEventsError !== "") {
    return <div>{loadEventsError}</div>;
  }

  return (
    <>
      <DetailsList
        items={eventsToRender}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: any): string => `${(item as IEvent).id}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        onRenderRow={onRenderRow}
        onRenderItemColumn={onRenderColumn}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => actions.updateCurrentPage(page)}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            actions.updatePageSize(op?.key as number);
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
      <Modal
        titleAriaId={"title"}
        isOpen={isModalOpen}
        isBlocking={false}
        containerClassName={modalStyles}
        topOffsetFixed
      >
        <ModalHeader>
          <Text block styles={modalHeaderStyles}>
            Event Details
          </Text>
          <IconButton
            styles={iconButtonStyles}
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close popup modal"
            onClick={onModalClose}
          />
        </ModalHeader>
        <ModalContent>
          <EventDetails closeModal={onModalClose} />
        </ModalContent>
      </Modal>
    </>
  );
};

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IEventsListProps & any) => ({
  data: ownProps.data,
  isLoadingEvents: isLoadingEvents(state.eventState),
  loadEventsError: getLoadEventsError(state.eventState),
  formSubmittingComplete: false,
  currentPage: getCurrentPage(state.eventState),
  pageSize: getPageSize(state.eventState),
  searchText: getSearchText(state.eventState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<EventActions.EventActionTypes>) => ({
  actions: {
    updateCurrentPage: bindActionCreators(EventActions.updateCurrentPage, dispatch),
    updatePageSize: bindActionCreators(EventActions.updatePageSize, dispatch), //Create new event action and bind new event
    updateSelectedEventId: bindActionCreators(EventActions.updateSelectedEventId, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EventsList);
